import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { HABIT_COLORS, type HabitsResponse } from '@td/shared';
import { useHabits, useToggleHabitDay, useCreateHabit, useDeleteHabit } from '../hooks/useHabits';
import { useConfirm } from './ConfirmDialog';
import { habitColor, themeVar } from '../theme/palette';

const DOW_INITIAL = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/*
 * Squares grow and shrink with the card rather than sitting at a fixed size.
 *
 * The bounds are what keeps that sensible: below about nine pixels a square is
 * too small to hit with a mouse, and above thirty the month starts looking
 * like a wall chart. Between those, the grid takes whatever room the card has.
 */
const MIN_CELL = 9;
const MAX_CELL = 30;
const CELL_GAP = 2;
const ROW_GAP = 6;

/**
 * Measures a box and re-measures whenever it changes size.
 *
 * A resize observer rather than a window listener, because the card is dragged
 * to a new size while the window stays exactly as it was.
 */
function useBoxSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}

/**
 * One row per habit, one square per day of the month, with a date header so
 * it's obvious which day is being marked.
 *
 * Filled = done, empty = not. Today is ringed. The month resets on its own
 * because a new month simply has no entries yet — and past months stay
 * browsable for the same reason: their documents are still there.
 */
export function HabitTracker({
  initial,
  todayYear,
  todayMonth,
  todayDay,
}: {
  initial: HabitsResponse;
  todayYear: number;
  todayMonth: number;
  todayDay: number;
}) {
  const [year, setYear] = useState(initial.year);
  const confirm = useConfirm();
  const [month, setMonth] = useState(initial.month);

  const isCurrentMonth = year === todayYear && month === todayMonth;
  const { data } = useHabits(year, month, isCurrentMonth ? initial : undefined);

  const toggle = useToggleHabitDay();
  const create = useCreateHabit();
  const remove = useDeleteHabit();

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [managing, setManaging] = useState(false);

  function step(delta: number) {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  function thisMonth() {
    setYear(todayYear);
    setMonth(todayMonth);
  }

  const isFuture = year > todayYear || (year === todayYear && month > todayMonth);
  const grid = data ?? initial;
  const days = Array.from({ length: grid.daysInMonth }, (_, i) => i + 1);

  const [boardRef, board] = useBoxSize<HTMLDivElement>();
  const labelRef = useRef<HTMLDivElement>(null);
  const [labelWidth, setLabelWidth] = useState(0);

  /*
   * The names column, measured off its empty header cell.
   *
   * It sits in the same grid column as the names, so the browser has already
   * worked out how wide it is — the number is only needed to decide how much
   * room is left for the squares. Measured rather than guessed, because the
   * teacher can change the font for the whole app and a hard-coded width was
   * only ever right for one of them.
   */
  useLayoutEffect(() => {
    setLabelWidth(labelRef.current?.offsetWidth ?? 0);
  }, [grid.habits, board.width, managing]);

  const rowCount = Math.max(grid.habits.length, 1);

  /*
   * Whatever is left over after the names, shared between the days — and
   * never so tall that the rows spill out of the card.
   *
   * There are daysInMonth gaps, not daysInMonth - 1: the names are a grid
   * column too, so there is a gap between them and the first square.
   */
  const widthCell =
    (board.width - labelWidth - CELL_GAP * grid.daysInMonth) / grid.daysInMonth;

  // 22px covers the two-line date header above the squares.
  const heightCell = (board.height - 22 - ROW_GAP * rowCount) / rowCount;

  const cell = Math.max(
    MIN_CELL,
    Math.min(MAX_CELL, Math.floor(Math.min(widthCell || MAX_CELL, heightCell || MAX_CELL))),
  );

  // A finished month is fully editable so you can backfill; a future one isn't
  // editable at all; the current month stops at today.
  const editableThrough = isFuture ? 0 : isCurrentMonth ? todayDay : grid.daysInMonth;

  const dowOf = (day: number) => new Date(grid.year, grid.month - 1, day).getDay();

  function addHabit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate({
      name: trimmed,
      color: HABIT_COLORS[grid.habits.length % HABIT_COLORS.length],
    });
    setName('');
    setAdding(false);
  }

  const monthLabel = new Date(grid.year, grid.month - 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    // Full height, so the board in the middle can take the room the card has.
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-3 flex shrink-0 items-center gap-1">
        <button
          onClick={() => step(-1)}
          className="rounded px-1.5 py-0.5 text-sm text-stone hover:bg-linen hover:text-espresso"
          aria-label="Previous month"
        >
          ←
        </button>
        <span className="min-w-[112px] text-center text-xs text-espresso">{monthLabel}</span>
        <button
          onClick={() => step(1)}
          disabled={isCurrentMonth}
          className="rounded px-1.5 py-0.5 text-sm text-stone hover:bg-linen hover:text-espresso disabled:cursor-default disabled:opacity-30"
          aria-label="Next month"
        >
          →
        </button>
        {!isCurrentMonth && (
          <button onClick={thisMonth} className="btn-soft-accent ml-1 px-1.5 py-0.5 text-[11px]">
            This month
          </button>
        )}
      </div>

      {grid.habits.length === 0 ? (
        <p className="text-sm text-stone">
          Track something small and daily — water, steps, reading, grading.
        </p>
      ) : (
        /*
          Horizontal scroll rather than wrapping: wrapping would break the
          alignment between the date header and the squares below it.

          The padding used to sit inside the scroller, which made the content
          8px wider than the box and left a scrollbar sitting there on a grid
          that fitted perfectly well. The padding moves outside; the scroller
          now overflows only when the month genuinely doesn't fit.
        */
        <div ref={boardRef} className="scroll-x min-h-0 flex-1 pb-1">
          {/*
            One grid for the whole board, header row included.

            Every row used to be its own flex line with its own name column,
            so a short name like "Grade" gave that row a narrower first column
            and shifted its squares left — the rows disagreed with each other
            and with the dates above them. Here there is a single set of
            columns: the name column is as wide as the longest name, every
            square lands under its date by construction, and no arithmetic is
            involved in keeping them there.

            Centred once the squares reach full size, so a very wide card has
            an even margin rather than a lake of empty on one side.
          */}
          <div
            className="mx-auto w-max"
            style={{
              display: 'grid',
              gridTemplateColumns: `max-content repeat(${grid.daysInMonth}, ${cell}px)`,
              columnGap: CELL_GAP,
              rowGap: ROW_GAP,
              alignItems: 'center',
            }}
          >
            {/* Header: an empty cell for the names, the dates, an empty cell for the tally. */}
            <div ref={labelRef} />
            {days.map((day) => {
              const dow = dowOf(day);
              const weekend = dow === 0 || dow === 6;
              const isToday = isCurrentMonth && day === todayDay;
              return (
                <div key={`head-${day}`} className="text-center leading-none">
                  <div
                    className={[
                      'text-[8px]',
                      isToday ? 'text-terracotta' : weekend ? 'text-sand' : 'text-stone',
                    ].join(' ')}
                  >
                    {DOW_INITIAL[dow]}
                  </div>
                  <div
                    className={[
                      'mt-[1px] text-[9px] tabular-nums',
                      isToday
                        ? 'font-medium text-terracotta'
                        : weekend
                          ? 'text-stone/60'
                          : 'text-walnut',
                    ].join(' ')}
                  >
                    {day}
                  </div>
                </div>
              );
            })}

            {grid.habits.map((habit) => {
              const entry = grid.entries.find((e) => e.habitId === habit.id);
              const marks = entry?.days ?? [];

              return (
                <Fragment key={habit.id}>
                  <div className="flex items-center gap-1 whitespace-nowrap pr-2">
                    <span className="text-xs text-espresso">{habit.name}</span>
                    {managing && (
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Delete "${habit.name}"?`,
                            body: 'Every square you have filled in for it goes too.',
                            confirmLabel: 'Delete',
                            danger: true,
                          });
                          if (ok) remove.mutate(habit.id);
                        }}
                        aria-label={`Delete ${habit.name}`}
                        className="shrink-0 text-stone hover:text-rust"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {days.map((day) => {
                    const done = marks[day - 1] === true;
                    const locked = day > editableThrough;
                    const label = new Date(grid.year, grid.month - 1, day).toLocaleDateString(
                      undefined,
                      { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
                    );

                    return (
                      <button
                        key={`${habit.id}-${day}`}
                        disabled={locked}
                        onClick={() =>
                          toggle.mutate({
                            id: habit.id,
                            year: grid.year,
                            month: grid.month,
                            day,
                            value: !done,
                          })
                        }
                        title={`${habit.name} — ${label}`}
                        aria-label={`${habit.name}, ${label}, ${done ? 'done' : 'not done'}`}
                        className={[
                          'border transition-colors',
                          locked ? 'cursor-default opacity-35' : 'cursor-pointer',
                          isCurrentMonth && day === todayDay
                            ? 'ring-1 ring-espresso ring-offset-[1px]'
                            : '',
                        ].join(' ')}
                        style={{
                          width: cell,
                          height: cell,
                          // Proportional, so a big square doesn't turn into a
                          // circle and a small one into a box.
                          borderRadius: Math.max(3, Math.round(cell * 0.28)),
                          // Through the theme's variables, so the squares
                          // recolour the instant the theme does.
                          backgroundColor: done ? habitColor(habit.color) : themeVar('cream'),
                          borderColor: done ? habitColor(habit.color) : themeVar('sand'),
                        }}
                      />
                    );
                  })}

                </Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-3 flex shrink-0 items-center gap-3">
        {adding ? (
          <input
            className="field py-1 text-sm"
            value={name}
            autoFocus
            placeholder="Drank water"
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            onBlur={addHabit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addHabit();
              if (e.key === 'Escape') {
                setName('');
                setAdding(false);
              }
            }}
          />
        ) : (
          <>
            <button onClick={() => setAdding(true)} className="btn-soft-accent">
              + Add habit
            </button>
            {grid.habits.length > 0 && (
              <button onClick={() => setManaging((v) => !v)} className="btn-soft">
                {managing ? 'Done' : 'Manage'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
